import { useState } from 'react';
import { submitRoundScore, getBoutScores } from '../../api/client';
import { useI18n } from '../../i18n';
import type { Translations } from '../../i18n';
import { fillTemplate } from '../../lib/fillTemplate';
import { useToast } from '../../hooks/useToast';
import { useBusy } from '../../hooks/useBusy';
import { useDeferredLoading } from '../../hooks/useDeferredLoading';
import { useLoad } from '../../hooks/useLoad';
import { useAuthStore } from '../../store/auth';
import { LockIcon, DotIcon } from '../shared/Icons';
import ErrorState from '../shared/ErrorState';
import type { Bout, RoundScore } from '../../types';
import { boutRounds } from '../../utils/tournament';
import Notice from '../shared/Notice';
import Field from '../shared/Field';
import { FOUGHT_VALUES, foughtFromCard, outcomeOf, tapPoints, type Corner, type RoundOutcome } from '../../lib/roundCard';

interface Props {
  bout: Bout;
  onScoreSubmitted?: () => void;
}

/** One round of the judge's card, as the form holds it (#469). The point
 *  buttons hold the round AS FOUGHT (IFMA v3.057 §29.2.3); each Warning then
 *  takes 1 point (§29.2.5), and the card stores the points after it. */
interface RoundState {
  red_fought: number;
  blue_fought: number;
  red_cautions: number;
  blue_cautions: number;
  red_warnings: number;
  blue_warnings: number;
  red_knockdowns: number;
  blue_knockdowns: number;
  /** The judge's tap on the outcome row; null = the points decide. */
  outcome_pick: RoundOutcome | null;
  saved: boolean; // own score exists in DB for this round
}

const DEFAULT_ROUND: RoundState = {
  red_fought: 10,
  blue_fought: 10,
  red_cautions: 0,
  blue_cautions: 0,
  red_warnings: 0,
  blue_warnings: 0,
  red_knockdowns: 0,
  blue_knockdowns: 0,
  outcome_pick: null,
  saved: false,
};

/** `{placeholder}` substitution for the strings of this card. */
const fill = (tpl: string, params: Record<string, string | number>) =>
  fillTemplate(tpl, params, useI18n.getState().t);

const cornerName = (t: Translations, c: Corner) => (c === 'red' ? t.common.red : t.common.blue);

export default function RoundScoring({ bout, onScoreSubmitted }: Props) {
  const { t } = useI18n();
  const { user } = useAuthStore();
  const { showToast, showError } = useToast();
  const [activeRound, setActiveRound] = useState(1);
  // #602 — 3 rounds, or 5 for a bout of a class whose max_rounds is 5.
  const roundCount = boutRounds(bout);
  const [rounds, setRounds] = useState<Record<number, RoundState>>(() =>
    Object.fromEntries([1, 2, 3, 4, 5].map(r => [r, { ...DEFAULT_ROUND }])));
  // #457 — the own cards did not load. Before, the judge saw an empty card
  // with no message, and could score a saved round again as new.
  // #453 — the own cards only. `scores` held the approved totals too,
  // so an approved round showed the official total as "your submitted
  // scores", and after an un-approve "Update" saved it as the card.
  const scores = useLoad(
    () => getBoutScores(bout.id).then(res => (res.data?.own_scores || []) as RoundScore[]),
    [bout.id, user?.id],
    {
      context: 'RoundScoring:load',
      initial: [] as RoundScore[],
      onData: mine => {
        if (mine.length === 0) return;
        setRounds(prev => {
          const next = { ...prev };
          mine.forEach(s => {
            const r = s.round_number;
            if (r >= 1 && r <= roundCount) {
              next[r] = {
                // #469 — the card holds the points after the Warnings; the
                // buttons hold the round as fought.
                red_fought:      foughtFromCard(s.red_points, s.red_warnings),
                blue_fought:     foughtFromCard(s.blue_points, s.blue_warnings),
                red_cautions:    s.red_cautions    ?? 0,
                blue_cautions:   s.blue_cautions   ?? 0,
                red_warnings:    s.red_warnings    ?? 0,
                blue_warnings:   s.blue_warnings   ?? 0,
                red_knockdowns:  s.red_knockdowns  ?? 0,
                blue_knockdowns: s.blue_knockdowns ?? 0,
                outcome_pick:    null,
                saved: true,
              };
            }
          });
          return next;
        });
      },
    },
  );
  // #458 — the loading text shows after 200 ms and holds 400 ms (useDeferredLoading):
  // a fast load shows nothing, not a one-frame flash.
  const showLoading = useDeferredLoading(scores.loading);
  // #605, #664 — the lock of Submit: a second tap sends no second card.
  const submit = useBusy();

  // Render-time: approved rounds come straight from the bout prop (kept
  // current by the parent's socket subscription). Previously this was
  // mirrored into local state via a prop→state useEffect — removed as
  // part of the hygiene audit (bucket 5, derived-state anti-pattern).
  const approvedRounds: number[] = bout.display_rounds ?? [];

  const current       = rounds[activeRound];
  const isApproved    = approvedRounds.includes(activeRound);
  // #469 — what the card will store, and the outcome that the points give.
  const derived       = outcomeOf(current.red_fought, current.blue_fought);
  const shownOutcome  = current.outcome_pick ?? derived;
  const mismatch      = current.outcome_pick !== null && current.outcome_pick !== derived;
  const redAfter      = current.red_fought - current.red_warnings;
  const blueAfter     = current.blue_fought - current.blue_warnings;
  // What to change, in the judge's words: the outcome confirms the points.
  const pick          = current.outcome_pick;
  const mismatchHint  = !mismatch || pick === null ? null
    : derived === 'even' && pick !== 'even' ? fill(t.scoring.mismatchEven, { corner: cornerName(t, pick) })
    : pick === 'even' ? t.scoring.mismatchNeedsEven
    : derived !== 'even' ? fill(t.scoring.mismatchCorner, { derived: cornerName(t, derived), corner: cornerName(t, pick) })
    : null;

  function updateCurrent(patch: Partial<RoundState>) {
    if (isApproved) return; // guard — should not be reachable via UI, but defence in depth
    setRounds(prev => ({
      ...prev,
      [activeRound]: { ...prev[activeRound], ...patch },
    }));
  }

  /** A point tap keeps one athlete at 10. It keeps the judge's outcome pick
   *  too: the pick confirms the points, so a disagreement blocks the submit
   *  until the points agree or the judge picks again (operator decision).
   *  Dropping the pick let a mis-tap after a confirmed outcome go through. */
  function tapPoint(corner: Corner, value: number) {
    const next = tapPoints(corner, value, { red: current.red_fought, blue: current.blue_fought });
    updateCurrent({ red_fought: next.red, blue_fought: next.blue });
  }

  function switchRound(r: number) {
    setActiveRound(r);
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (isApproved || mismatch) return;
    await submit.run(async () => {
      try {
        await submitRoundScore(bout.id, {
          round_number: activeRound,
          red_points:       redAfter,
          blue_points:      blueAfter,
          red_cautions:     current.red_cautions,
          blue_cautions:    current.blue_cautions,
          red_warnings:     current.red_warnings,
          blue_warnings:    current.blue_warnings,
          red_knockdowns:   current.red_knockdowns,
          blue_knockdowns:  current.blue_knockdowns,
          round_outcome:    derived,
        });
        setRounds(prev => ({
          ...prev,
          [activeRound]: { ...prev[activeRound], saved: true },
        }));
        showToast(fill(t.scoring.savedToast, { n: activeRound, red: redAfter, blue: blueAfter }), 'success');
        onScoreSubmitted?.();
      } catch (err) {
        showError(err);
      }
    });
  }

  if (showLoading) {
    return <div className="card p-6 text-center text-gray-400 text-sm">{t.common.loading}</div>;
  }
  if (scores.loading) return null;
  if (scores.error) return <ErrorState compact onRetry={scores.reload} />;

  return (
    // A role tag tells the side judge's card from the jury's (operator,
    // 2026-09-25: the two looked the same).
    <div className="card p-4 space-y-5" data-testid="side-judge-card">

      {/* Header + round tabs */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm text-gray-900 flex items-center gap-2">{t.bout.roundScore} <span className="badge-green">{t.bout.sideJudge}</span></h3>
          <p className="text-xs text-gray-400 mt-0.5">{t.scoring.pointsRange}</p>
        </div>
        <div className="flex gap-1">
          {Array.from({ length: roundCount }, (_, i) => i + 1).map(r => {
            const approved = approvedRounds.includes(r);
            const saved    = rounds[r].saved;
            return (
              <button
                key={r}
                type="button"
                data-testid={`score-round-tab-${r}`}
                onClick={() => switchRound(r)}
                className={`w-9 h-9 rounded-full text-sm font-bold transition-all relative
                  ${activeRound === r
                    ? approved
                      ? 'bg-amber-500 text-white shadow-md'
                      : 'bg-gray-900 text-white shadow-md'
                    : approved
                      ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {r}
                {/* Green dot = saved, amber lock = jury-approved */}
                {approved ? (
                  <span className="absolute -top-0.5 -right-0.5 leading-none text-amber-700"><LockIcon className="w-2.5 h-2.5" /></span>
                ) : saved ? (
                  <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-green-500 rounded-full border-2 border-white" />
                ) : null}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── Approved round: locked view ────────────────────────────────────── */}
      {isApproved ? (
        <div className="space-y-4">
          {/* Lock banner */}
          <div className="flex items-center gap-2.5 px-4 py-3 bg-amber-50 border border-amber-200 rounded-xl">
            <LockIcon className="w-6 h-6 shrink-0 text-amber-700" />
            <div>
              <div className="text-sm font-semibold text-amber-800">{fill(t.scoring.approvedTitle, { n: activeRound })}</div>
              <div className="text-xs text-amber-600 mt-0.5">{t.scoring.approvedText}</div>
            </div>
          </div>

          {/* Read-only card */}
          {current.saved ? (
            <div className="space-y-3">
              <RoundResult s={current} t={t} />
              {(current.red_cautions > 0 || current.blue_cautions > 0 ||
                current.red_knockdowns > 0 || current.blue_knockdowns > 0) && (
                <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                  {current.red_cautions > 0   && <span className="inline-flex items-center gap-1"><DotIcon className="w-4 h-4 text-red-500" /> {t.common.cautions}: {current.red_cautions}</span>}
                  {current.blue_cautions > 0  && <span className="inline-flex items-center gap-1"><DotIcon className="w-4 h-4 text-blue-500" /> {t.common.cautions}: {current.blue_cautions}</span>}
                  {current.red_knockdowns > 0  && <span className="inline-flex items-center gap-1"><DotIcon className="w-4 h-4 text-red-500" /> {t.common.knockdowns}: {current.red_knockdowns}</span>}
                  {current.blue_knockdowns > 0 && <span className="inline-flex items-center gap-1"><DotIcon className="w-4 h-4 text-blue-500" /> {t.common.knockdowns}: {current.blue_knockdowns}</span>}
                </div>
              )}
            </div>
          ) : (
            <p className="text-xs text-gray-400 text-center py-2">{t.scoring.notSubmitted}</p>
          )}
        </div>
      ) : (
        /* ── Editable round ──────────────────────────────────────────────── */
        <>
          {/* Saved indicator */}
          {current.saved && (
            <Notice tone="success" size="xs" icon className="font-medium">{fill(t.scoring.savedHint, { n: activeRound })}</Notice>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">

            {/* Point selectors — the round as fought (§29.2.3) */}
            <div className="space-y-3">
              {(['red', 'blue'] as const).map(corner => {
                const value = corner === 'red' ? current.red_fought : current.blue_fought;
                return (
                  <Field group key={corner} label={
                    <span className={`${corner === 'red' ? 'text-red-600' : 'text-blue-600'} inline-flex items-center gap-1`}>
                      <DotIcon className="w-5 h-5" /> {fill(t.scoring.cornerPoints, { corner: cornerName(t, corner) })}
                    </span>}>
                    <div className="flex gap-2" data-score-row={`${corner}-points`}>
                      {FOUGHT_VALUES.map(v => (
                        <button key={v} type="button" onClick={() => tapPoint(corner, v)}
                          className={`flex-1 py-3 rounded-xl text-base font-black border-2 transition-all
                            ${value === v
                              ? corner === 'red'
                                ? 'bg-red-600 border-red-600 text-white shadow-md scale-105'
                                : 'bg-blue-600 border-blue-600 text-white shadow-md scale-105'
                              : corner === 'red'
                                ? 'border-red-200 text-red-700 hover:border-red-400 hover:bg-red-50'
                                : 'border-blue-200 text-blue-700 hover:border-blue-400 hover:bg-blue-50'}`}>
                          {v}
                        </button>
                      ))}
                    </div>
                  </Field>
                );
              })}
            </div>

            {/* Warnings — each takes 1 point (§29.2.5) */}
            <div>
              <p className="text-xs text-gray-500 mb-1">{t.scoring.warningsHint}</p>
              <div className="grid grid-cols-2 gap-3">
                <CountInput row="red-warnings" label={<span className="inline-flex items-center gap-1"><DotIcon className="w-5 h-5 text-red-500" /> {t.common.warnings}</span>} value={current.red_warnings}
                  onChange={v => updateCurrent({ red_warnings: v })} />
                <CountInput row="blue-warnings" label={<span className="inline-flex items-center gap-1"><DotIcon className="w-5 h-5 text-blue-500" /> {t.common.warnings}</span>} value={current.blue_warnings}
                  onChange={v => updateCurrent({ blue_warnings: v })} />
              </div>
            </div>

            {/* Cautions — a record only */}
            <div className="grid grid-cols-2 gap-3">
              <CountInput row="red-cautions" label={<span className="inline-flex items-center gap-1"><DotIcon className="w-5 h-5 text-red-500" /> {t.common.cautions}</span>} value={current.red_cautions}
                onChange={v => updateCurrent({ red_cautions: v })} />
              <CountInput row="blue-cautions" label={<span className="inline-flex items-center gap-1"><DotIcon className="w-5 h-5 text-blue-500" /> {t.common.cautions}</span>} value={current.blue_cautions}
                onChange={v => updateCurrent({ blue_cautions: v })} />
            </div>

            {/* Knockdowns — a record only (§32.2.3) */}
            <div className="grid grid-cols-2 gap-3">
              <CountInput row="red-knockdowns" label={<span className="inline-flex items-center gap-1"><DotIcon className="w-5 h-5 text-red-500" /> {t.common.knockdowns}</span>} value={current.red_knockdowns}
                onChange={v => updateCurrent({ red_knockdowns: v })} />
              <CountInput row="blue-knockdowns" label={<span className="inline-flex items-center gap-1"><DotIcon className="w-5 h-5 text-blue-500" /> {t.common.knockdowns}</span>} value={current.blue_knockdowns}
                onChange={v => updateCurrent({ blue_knockdowns: v })} />
            </div>

            <RoundResult s={current} t={t} />

            {/* Round outcome — last, pre-selected from the points */}
            <Field group label={t.scoring.roundOutcome}>
              <div className="flex gap-2" data-score-row="outcome">
                {(['red', 'even', 'blue'] as const).map(o => {
                  const selected = shownOutcome === o;
                  return (
                    <button key={o} type="button" data-outcome={o} aria-pressed={selected}
                      onClick={() => updateCurrent({ outcome_pick: o })}
                      className={`flex-1 py-2.5 rounded-xl text-sm font-bold border-2 transition-all
                        ${selected
                          ? o === 'red' ? 'bg-red-600 border-red-600 text-white shadow-md'
                            : o === 'blue' ? 'bg-blue-600 border-blue-600 text-white shadow-md'
                            : 'bg-gray-700 border-gray-700 text-white shadow-md'
                          : o === 'red' ? 'border-red-200 text-red-700 hover:bg-red-50'
                            : o === 'blue' ? 'border-blue-200 text-blue-700 hover:bg-blue-50'
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                      {o === 'even' ? t.scoring.even : cornerName(t, o)}
                    </button>
                  );
                })}
              </div>
              {mismatch && (
                <Notice tone="error" plain size="xs" className="mt-1.5" data-outcome-mismatch>{mismatchHint ?? t.errors.ROUND_OUTCOME_MISMATCH}</Notice>
              )}
            </Field>

            {/* A full-size button with a 48 px touch target: the judge taps it
                on a phone or a tablet at ringside, beside the tall point
                buttons (operator, 2026-09-24; the one exception to btn-sm on
                a scoring screen, code-patterns.md § Button sizing). */}
            <button type="submit" disabled={submit.busy || mismatch}
              className="btn-primary w-full min-h-12">
              {submit.busy ? t.common.loading
                : current.saved ? fill(t.scoring.updateRound, { n: activeRound })
                : fill(t.scoring.submitRound, { n: activeRound })}
            </button>
          </form>
        </>
      )}
    </div>
  );
}

// ── The result of the round, live (#469) ────────────────────────────────────
// The round as fought, the Warning deductions, and the points the card
// stores. A Warning changes the points, not who won the round: Red wins
// 10–9 and gets a Warning, the card holds 9–9, and Red won the round.
function RoundResult({ s, t }: { s: RoundState; t: Translations }) {
  const outcome = outcomeOf(s.red_fought, s.blue_fought);
  const diff    = Math.abs(s.red_fought - s.blue_fought);
  const title   = outcome === 'even'
    ? t.scoring.evenRound
    : fill(t.scoring.wonRound, { corner: cornerName(t, outcome), score: `10–${Math.min(s.red_fought, s.blue_fought)}` });
  const hint = diff === 0 ? t.bout.score_hint_10_10
    : diff === 1 ? t.bout.score_hint_10_9
    : diff === 2 ? t.bout.score_hint_10_8
    : t.bout.score_hint_10_7;
  const deductions = (['red', 'blue'] as const)
    .filter(c => (c === 'red' ? s.red_warnings : s.blue_warnings) > 0)
    .map(c => fill(t.scoring.warningDeduction, { corner: cornerName(t, c), n: c === 'red' ? s.red_warnings : s.blue_warnings }));
  const colorClass = outcome === 'red' ? 'bg-red-50 text-red-700' : outcome === 'blue' ? 'bg-blue-50 text-blue-700' : 'bg-gray-50 text-gray-600';

  return (
    <div className={`px-3 py-2.5 rounded-lg text-xs flex items-center gap-3 ${colorClass}`} data-round-result={outcome}>
      <div className="shrink-0 text-center">
        <div className="text-[10px] uppercase tracking-wide opacity-75">{t.scoring.afterWarnings}</div>
        <div className="flex items-center justify-center gap-2">
          <span className="text-xl font-black text-red-600 font-mono" data-after="red">{s.red_fought - s.red_warnings}</span>
          <span className="text-gray-400 font-bold">–</span>
          <span className="text-xl font-black text-blue-600 font-mono" data-after="blue">{s.blue_fought - s.blue_warnings}</span>
        </div>
      </div>
      <div>
        <div className="font-semibold">{title}</div>
        {deductions.length > 0 && <div className="mt-0.5 font-medium">{deductions.join(' · ')}</div>}
        <div className="opacity-75 mt-0.5 leading-snug">{hint}</div>
      </div>
    </div>
  );
}

// ── Count input ───────────────────────────────────────────────────────────────
function CountInput({ label, value, onChange, row }: {
  label: React.ReactNode; value: number; onChange: (v: number) => void; row: string;
}) {
  return (
    <Field group label={label}>
      <div className="flex items-center gap-1" data-score-row={row}>
        {[0, 1, 2, 3].map(n => (
          <button key={n} type="button" onClick={() => onChange(n)}
            className={`flex-1 h-9 rounded-md text-sm font-bold border transition-all ${
              value === n
                ? 'bg-gray-900 text-white border-gray-900'
                : 'border-gray-200 text-gray-600 hover:border-gray-400'}`}>
            {n}
          </button>
        ))}
      </div>
    </Field>
  );
}
